import { useApi } from '@src/app/context/Api';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import React, { useState } from 'react';

import { Styles } from './includes/styles';

/**
 * EditNote
 *
 * Displays a modal to edit a note on the risk record.
 *
 * @param eid
 * @param handleSearch
 * @param isOpen
 * @param note
 * @param setIsOpen
 * @returns {Element}
 * @constructor
 */
export function EditNote({ eid, handleSearch, isOpen, note, setIsOpen }) {
  const [isUpdating, setIsUpdating] = useState(false);
  const { clearApiCache, useApiMutation } = useApi('RiskRecord');
  const [RiskRecordNoteUpdate] = useApiMutation('RiskRecordNoteUpdate');

  const defaultValues = {
    eid,
    note,
  };

  const form = useForm({ defaultValues });
  const { toast } = useToast();

  /**
   * handleSubmit
   *
   * Handles the form submission.
   *
   * @returns {Promise<boolean>}
   */
  const handleSubmit = async (data) => {
    setIsUpdating(true);

    await RiskRecordNoteUpdate(data, {
      onError: () => {
        toast.show({
          message: 'Unable to Edit Note.',
          title: 'Edit Note Failed',
          variant: 'error',
        });
      },
      onSuccess: async () => {
        setIsUpdating(false);
        setIsOpen(false);
        await clearApiCache([{ apiKey: 'RiskRecord', queryKeys: ['GetRiskRecord'] }]);
        handleSearch();
        toast.show({
          message: 'Note has been successfully edited.',
          title: 'Edited note to EID',
          variant: 'success',
        });
        setIsUpdating(false);
        setIsOpen(false);
      },
    });
  };

  return (
    <ErrorHandler location="src/features/EidSearch/EditNote/EditNote.jsx">
      <Modal
        isOpen={isOpen}
        onClose={() => {
          return setIsOpen(false);
        }}
        title="Edit Note"
      >
        <FormProvider autoComplete="off" onSubmit={handleSubmit} state={form}>
          <Modal.Section>
            <Styles>
              <Grid>
                <Grid.Col span={{ xs: 12 }}>
                  <TextInputArea
                    label="Note"
                    model="note"
                    rows={3}
                    validators={{ required: 'Please enter a note' }}
                    value={note}
                  />
                </Grid.Col>
              </Grid>
            </Styles>
          </Modal.Section>
          <Modal.Section>
            <Divider height={1} />
            <Layout.Group alignLayout="right">
              <Button
                onClick={() => {
                  setIsOpen(false);
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button ariaLoadingLabel="Editing Note" isLoading={isUpdating} type="submit" variant="solid">
                Edit
              </Button>
            </Layout.Group>
          </Modal.Section>
        </FormProvider>
      </Modal>
    </ErrorHandler>
  );
}
